import isEqual from 'fast-deep-equal'

import type { Hunt } from '@cyborg-monorepo/hunter/common'

const DRAFT_KEY_PREFIX = 'hunt-draft-'

/**
 * A minimal storage shape so callers/tests can pass anything Storage-like
 * (including `localStorage` itself) without a hard runtime dependency on
 * the DOM lib types.
 */
export interface DraftStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

/**
 * Scoped by both user and hunt: localStorage persists across logins in the
 * same browser, so a key scoped only by hunt id could otherwise surface one
 * user's failed-save draft as a "recover unsaved changes?" prompt for a
 * different user who later opens the same hunt on that machine.
 */
export function huntDraftKey(userId: number | string, huntId: string): string {
  return `${DRAFT_KEY_PREFIX}${userId}-${huntId}`
}

/**
 * Write-only local recovery backup for a hunt snapshot. This is
 * intentionally never treated as a source of truth (see HUN-1091) -- it
 * exists solely so a failed/interrupted save isn't lost forever, and is
 * only ever offered back to the user via an explicit recovery prompt.
 */
export function writeHuntDraft(
  hunt: Hunt,
  userId: number | string,
  storage: DraftStorage,
): void {
  try {
    storage.setItem(huntDraftKey(userId, hunt.id), JSON.stringify(hunt))
  } catch (err) {
    console.error('hunt-draft-store.writeHuntDraft', err)
  }
}

export function clearHuntDraft(
  userId: number | string,
  huntId: string,
  storage: DraftStorage,
): void {
  try {
    storage.removeItem(huntDraftKey(userId, huntId))
  } catch (err) {
    console.error('hunt-draft-store.clearHuntDraft', err)
  }
}

export function readHuntDraft(
  userId: number | string,
  huntId: string,
  storage: DraftStorage,
): Hunt | null {
  try {
    const raw = storage.getItem(huntDraftKey(userId, huntId))
    return raw ? (JSON.parse(raw) as Hunt) : null
  } catch (err) {
    console.error('hunt-draft-store.readHuntDraft', err)
    return null
  }
}

/**
 * Decide whether a local draft should be offered to the user as a
 * recoverable change. Only true when a draft exists AND it differs from
 * what the server just returned -- an identical draft is stale, not a
 * recovery candidate.
 */
export function shouldOfferDraftRecovery(
  serverHunt: Hunt | null | undefined,
  draft: Hunt | null,
): boolean {
  if (!draft || !serverHunt) return false
  return !isEqual(draft, serverHunt)
}
